import { Outlet, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { CalendarOrientation } from '@complan/domain';
import type { ExportJob } from '@complan/contracts';
import { usePageChrome } from '@/components/layout/page-chrome';
import { parseCalendarSearch, resetFilters } from '@/shared/url/calendar-search-params';
import { Button } from '@/components/ui/button';
import { useCalendarBars, useSalesCycles } from './calendar-data';
import { buildCalendarGrid } from './grid-model';
import { CalendarGrid, type CalendarGridHandle } from './CalendarGrid';
import { FilterBar } from './FilterBar';
import { FilterChips } from './FilterChips';
import { useCalendarFilterOptions } from './calendar-options';
import {
  isTerminalExportState,
  toFilteredViewRequest,
  useExportJob,
  useRequestFilteredViewExport,
} from './export-data';

/**
 * The Calendar_View parent route (`/calendar/:year`).
 *
 * This route owns the filter/orientation/sort state, and that state lives entirely
 * in the URL: the year in the path segment, everything else in the search params
 * (design.md, "State ownership"). Because `/calendar/:year/initiative/:id` is a
 * *nested* route, this component stays mounted while the Detail_Panel is open, so
 * the filters, orientation, sort and the eventual scroll offset survive the
 * navigation structurally, with no save/restore code (R11.13). The Detail_Panel
 * renders through the `<Outlet />` below.
 *
 * Task 22.2 lands the three orientation grids (month, calendar-week, Sales_Cycle).
 * The two server reads — bars/count/legend and the year's Sales_Cycle definitions
 * — are keyed so that switching orientation re-derives the grid from cache with
 * no refetch (R10.2): the bars key folds orientation out, and the cycles key is
 * the year alone. The bar layer, milestone layer and legend are task 22.3; the
 * grid here leaves a per-lane track seam for them.
 */
export function CalendarPage(): React.JSX.Element {
  const params = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [search, setSearch] = useSearchParams();

  const parsedYear = Number.parseInt(params['year'] ?? '', 10);
  const year = Number.isFinite(parsedYear) ? parsedYear : new Date().getFullYear();
  const view = useMemo(() => parseCalendarSearch(year, search), [year, search]);
  const orientation = view.query.orientation;

  usePageChrome({
    title: t('calendar.title'),
    subtitle: t('calendar.subtitle', {
      year: view.query.planningYear,
      orientation: orientationLabel(orientation, t),
    }),
  });

  const barsQuery = useCalendarBars(view.query);
  const cyclesQuery = useSalesCycles(view.query.planningYear);

  // The filtered-view export (R13.6, R15.1): a request enqueues a job, and the
  // job is polled until it terminates (R18.4). The button carries the current
  // filter/sort — the same URL state the calendar read ran under — so the export
  // and the screen agree by construction (Property 8).
  const requestExport = useRequestFilteredViewExport();
  const [exportJobId, setExportJobId] = useState<string | null>(null);
  const exportJob = useExportJob(exportJobId);
  const exportRunning =
    requestExport.isPending ||
    (exportJob.data !== undefined && !isTerminalExportState(exportJob.data.state));

  const startExport = useCallback((): void => {
    requestExport.mutate(toFilteredViewRequest(view.query, view.sort), {
      onSuccess: (job) => setExportJobId(job.jobId),
    });
  }, [requestExport, view.query, view.sort]);

  // Every filter control mutates the URL — the single source of truth — replacing
  // the current search so a filter change is not a distinct history entry.
  const applySearch = useCallback(
    (next: URLSearchParams): void => {
      setSearch(next, { replace: true });
    },
    [setSearch],
  );

  const resetFiltersToEntry = useCallback((): void => {
    setSearch(resetFilters(search), { replace: true });
  }, [search, setSearch]);

  // Changing the planning year changes the route path (R11.1), carrying the rest
  // of the search string across so the other filters, orientation and sort stay.
  const changeYear = useCallback(
    (nextYear: number): void => {
      if (!Number.isFinite(nextYear)) return;
      const query = search.toString();
      navigate(`/calendar/${String(nextYear)}${query ? `?${query}` : ''}`);
    },
    [navigate, search],
  );

  // Opening a Saved_View applies its stored year, filters, orientation and sort by
  // writing them to the URL (R11.11): navigate to the view's year with its search.
  const applySavedView = useCallback(
    (nextYear: number, nextSearch: URLSearchParams): void => {
      const query = nextSearch.toString();
      navigate(`/calendar/${String(nextYear)}${query ? `?${query}` : ''}`);
    },
    [navigate],
  );

  // The grid model is a pure re-derivation over the cached payloads. It recomputes
  // when the orientation changes without either query refetching (R10.2).
  const model = useMemo(() => {
    if (!barsQuery.data) return null;
    return buildCalendarGrid({
      orientation,
      planningYear: view.query.planningYear,
      bars: barsQuery.data.bars,
      legend: barsQuery.data.legend,
      count: barsQuery.data.count,
      cycles: cyclesQuery.data ?? [],
      today: new Date(),
    });
  }, [barsQuery.data, cyclesQuery.data, orientation, view.query.planningYear]);

  // The calendar grid's imperative handle, used to return focus to the originating
  // bar when the Detail_Panel closes (task 22.4, §9 gap #2). The nested route's
  // `:id` param is present exactly while the panel is open; when it transitions
  // from present to absent the panel has closed, so we ask the grid to restore
  // focus to the bar the user activated.
  const gridHandle = useRef<CalendarGridHandle | null>(null);
  const openInitiativeId = params['id'] ?? null;
  const previousOpenId = useRef<string | null>(openInitiativeId);
  useEffect(() => {
    if (previousOpenId.current !== null && openInitiativeId === null) {
      gridHandle.current?.notifyDetailClosed();
    }
    previousOpenId.current = openInitiativeId;
  }, [openInitiativeId]);

  // Opening the Detail_Panel is a nested-route navigation that preserves the
  // search string (filters, orientation, sort), so the calendar never unmounts
  // (R11.13). Pointer click and keyboard Enter/Space both route through here
  // (R10.13); the roving-focus controller lives in the grid.
  const openInitiative = useCallback(
    (initiativeId: string): void => {
      const query = search.toString();
      navigate(`/calendar/${String(year)}/initiative/${initiativeId}${query ? `?${query}` : ''}`);
    },
    [navigate, search, year],
  );

  function selectOrientation(next: CalendarOrientation): void {
    setSearch(
      (current) => {
        const params = new URLSearchParams(current);
        if (next === 'month') {
          params.delete('orientation');
        } else {
          params.set('orientation', next);
        }
        return params;
      },
      { replace: true },
    );
  }

  // Named values for the master-data filters and Owner (R11.2-R11.4), and the
  // id -> name resolver the chip row uses so a chip reads "Platform: P1" rather
  // than a UUID (§3.7).
  const filterOptions = useCalendarFilterOptions(view.query, cyclesQuery.data ?? []);

  const resultCount = model?.count ?? barsQuery.data?.count ?? null;

  return (
    <div className="flex gap-3">
      <section className="min-w-0 flex-1">
        {/* The single filter bar that drives KPIs, grid, chips and export (§3.6). */}
        <FilterBar
          query={view.query}
          sort={view.sort}
          search={search}
          salesCycles={cyclesQuery.data ?? []}
          resultCount={resultCount}
          orientation={orientation}
          onOrientationChange={selectOrientation}
          options={filterOptions}
          onSearchChange={applySearch}
          onYearChange={changeYear}
          onApplySavedView={applySavedView}
          onReset={resetFiltersToEntry}
          onExport={startExport}
          isExporting={exportRunning}
        />

        {/* Active filters as chips, terminating with the mandatory result-count
            chip — how R11.9's count surfaces visually (§3.7). Rendered even at
            zero, which is a legitimate result the user must see stated. */}
        {resultCount !== null ? (
          <div className="mb-[10px]">
            <FilterChips
              query={view.query}
              count={resultCount}
              search={search}
              onSearchChange={applySearch}
              resolveLabel={filterOptions.resolveLabel}
            />
          </div>
        ) : null}

        <ExportStatus
          state={exportJob.data?.state ?? null}
          isError={exportJob.isError || requestExport.isError}
        />

        {/* A plain `--panel` surface rather than a `Card`, and no inner padding:
            `CalendarGrid` already draws its own bordered, radius-clipped frame, so
            a Card's border plus 14px inset boxed an already-boxed thing twice. The
            surface itself stays because the grid's interior is transparent — its
            lanes paint no background of their own — and because the loading and
            error states need something to sit on. They bring their own padding;
            the grid needs none and now fills the surface edge to edge. */}
        <div className="bg-card rounded-[var(--radius-card)]">
          <CalendarBody
            orientation={orientation}
            isLoading={barsQuery.isLoading}
            isError={barsQuery.isError}
            model={model}
            onRetry={() => void barsQuery.refetch()}
            onOpenInitiative={openInitiative}
            gridHandleRef={gridHandle}
          />
        </div>
      </section>
      {/* The Detail_Panel (task 23.4) mounts here without unmounting the calendar. */}
      <Outlet />
    </div>
  );
}

interface CalendarBodyProps {
  readonly orientation: CalendarOrientation;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly model: ReturnType<typeof buildCalendarGrid> | null;
  readonly onRetry: () => void;
  readonly onOpenInitiative: (initiativeId: string) => void;
  readonly gridHandleRef: React.Ref<CalendarGridHandle>;
}

function CalendarBody({
  orientation,
  isLoading,
  isError,
  model,
  onRetry,
  onOpenInitiative,
  gridHandleRef,
}: CalendarBodyProps): React.JSX.Element {
  const { t } = useTranslation();
  // The grid-less states carry their own inset: the surface around them no longer
  // pads its children, because the grid itself must sit flush against its edges.
  if (isError) {
    return (
      <div className="text-muted-foreground flex flex-col items-start gap-2 px-[14px] py-10 text-[12.3px]">
        <p>{t('calendar.loadError')}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  if (isLoading || !model) {
    return (
      <p className="text-muted-foreground px-[14px] py-10 text-[12.3px]" role="status">
        {t('calendar.loading', { orientation: t(ORIENTATION_LABEL_LOWER_KEYS[orientation]) })}
      </p>
    );
  }

  return (
    <div className="flex flex-col">
      <CalendarGrid
        model={model}
        onOpenInitiative={onOpenInitiative}
        handleRef={gridHandleRef}
      />
      {/* The empty-result message of R10.16: the full-year grid still renders
          above; here we state that nothing matched and show a zero count. This is
          indistinguishable from an all-confidential result by construction (R3.2).
          Padded on its own, since the surface no longer insets its children. */}
      {model.isEmpty ? (
        <p className="text-muted-foreground px-[14px] py-3 text-[12.3px]" role="status">
          {t('calendar.empty', { year: model.planningYear })}
        </p>
      ) : null}
    </div>
  );
}


/**
 * A compact live status for the filtered-view export job. An export is a job, not
 * an immediate download (R14.11, R15.9): this states where the job is — queued,
 * running, ready, or failed — while the calendar stays interactive underneath.
 */
function ExportStatus({
  state,
  isError,
}: {
  state: ExportJob['state'] | null;
  isError: boolean;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  if (isError) {
    return (
      <p className="mb-[10px] text-[11.5px] text-[var(--a-hard-txt)]" role="status">
        {t('calendar.export.startFailed')}
      </p>
    );
  }
  if (state === null) {
    return null;
  }
  const message =
    state === 'queued' || state === 'running'
      ? t('calendar.export.preparing')
      : state === 'succeeded'
        ? t('calendar.export.ready')
        : state === 'timed_out'
          ? t('calendar.export.timedOut')
          : t('calendar.export.failed');
  return (
    <p className="text-muted-foreground mb-[10px] text-[11.5px]" role="status" aria-live="polite">
      {message}
    </p>
  );
}

const ORIENTATION_LABEL_KEYS: Record<CalendarOrientation, string> = {
  month: 'calendar.orientation.month',
  'calendar-week': 'calendar.orientation.calendarWeek',
  'sales-cycle': 'calendar.orientation.salesCycle',
};

const ORIENTATION_LABEL_LOWER_KEYS: Record<CalendarOrientation, string> = {
  month: 'calendar.orientation.monthLower',
  'calendar-week': 'calendar.orientation.calendarWeekLower',
  'sales-cycle': 'calendar.orientation.salesCycleLower',
};


/** The orientation's display label, from the catalogue (R18.9). */
function orientationLabel(orientation: CalendarOrientation, t: TFunction): string {
  return t(ORIENTATION_LABEL_KEYS[orientation]);
}
